/**
 * Painel do dia da tela de Pagamentos: quanto do dinheiro que entrou já foi identificado.
 *
 * Sem biblioteca de gráficos — o projeto não tem nenhuma, e não vale adicionar uma por duas
 * formas. As barras são `div` com largura percentual; a rosca é SVG com `stroke-dasharray`.
 *
 * Barras: uma por CONTA DE RECEBIMENTO, mais uma para cada tipo não-financeiro presente no dia
 * (confissão de dívida e dação não têm conta — `receiving_bank_account_id` é nulo neles). Grupo
 * sem pagamento no dia não vira barra: linha zerada ocupa espaço afirmando nada.
 *
 * Os três estados somam 100% por construção — mas a soma agora é de VALOR, não de pagamentos. Desde
 * o step 4 um pagamento pode estar nos três ao mesmo tempo, e é a repartição dele (efetivado,
 * marcado, a marcar) que entra na barra. Ler a barra como "quantos pagamentos estão em cada passo"
 * passou a ser errado; ela responde "quanto do dinheiro do dia está em cada passo".
 */
import { useMemo } from 'react';
import { PaymentRow, PaymentStatus } from './services/billingApi';
import { formatBRL } from '../../../utils/currencyUtils';

/** Rótulo e cor de cada estado. Verde = terminou; âmbar = esperando alguém. */
export const STATUS_META: Record<PaymentStatus, { label: string; bar: string; hex: string }> = {
    PENDING: { label: 'Pendente', bar: 'bg-slate-400', hex: '#94a3b8' },
    MARKED: { label: 'Sinalizado', bar: 'bg-amber-400', hex: '#fbbf24' },
    IDENTIFIED: { label: 'Identificado', bar: 'bg-green-500', hex: '#22c55e' },
};

const ORDEM: PaymentStatus[] = ['PENDING', 'MARKED', 'IDENTIFIED'];

/** Rótulo do grupo não-financeiro — o pagamento não tem conta, mas tem natureza. */
const TIPO_SEM_CONTA: Record<string, string> = {
    DEBT_CONFESSION: 'Confissão de Dívida',
    ASSET_DATION: 'Dação de Bens',
};

export interface Grupo {
    key: string;
    label: string;
    total: number;
    porStatus: Record<PaymentStatus, number>;
}

/**
 * Quanto de UM pagamento está em cada passo.
 *
 * ⚠️ Antes o painel jogava o valor INTEIRO do pagamento no balde do status dele. Funcionava
 * enquanto um pagamento só podia estar num passo de cada vez; com marcação e efetivação parciais
 * (AF-3671 step 4), um pagamento de 1.000 com 200 efetivados e 300 marcados ia inteiro para
 * "pendente" — o painel afirmava que nada daquele dinheiro tinha destino.
 *
 * O fallback existe para resposta antiga em cache não zerar o painel enquanto o deploy não passa:
 * sem os campos novos, cai no comportamento anterior.
 */
export const splitByStep = (row: PaymentRow): Record<PaymentStatus, number> => {
    const total = row.amount || 0;
    if (row.pendingAmount == null) {
        return { PENDING: 0, MARKED: 0, IDENTIFIED: 0, [row.status]: total } as Record<PaymentStatus, number>;
    }
    return {
        PENDING: row.pendingAmount,
        MARKED: row.scheduledAmount || 0,
        IDENTIFIED: row.paidAmount || 0,
    };
};

/**
 * Reparte os pagamentos do dia em grupos.
 *
 * O critério muda com o tipo de propósito: financeiro agrupa por CONTA (é onde o dinheiro caiu),
 * e o resto agrupa por TIPO (não caiu em conta nenhuma). Misturar os dois numa cesta "sem conta"
 * esconderia que confissão e dação são naturezas diferentes.
 */
export const agruparPagamentos = (rows: PaymentRow[]): Grupo[] => {
    const grupos = new Map<string, Grupo>();

    for (const row of rows) {
        const semConta = TIPO_SEM_CONTA[row.paymentType];
        const label = semConta || row.receivingBankAccount?.exibitionName || 'Sem conta';
        const key = semConta ? row.paymentType : label;

        if (!grupos.has(key)) {
            grupos.set(key, {
                key, label, total: 0,
                porStatus: { PENDING: 0, MARKED: 0, IDENTIFIED: 0 },
            });
        }
        const grupo = grupos.get(key)!;
        const parts = splitByStep(row);
        grupo.total += row.amount || 0;
        for (const status of ORDEM) grupo.porStatus[status] += parts[status];
    }

    // financeiro primeiro (é o volume), e dentro de cada bloco por valor decrescente
    // Array.from e não spread: o target do tsconfig não permite iterar Map diretamente
    return Array.from(grupos.values()).sort((a, b) => {
        const aSemConta = a.key in TIPO_SEM_CONTA ? 1 : 0;
        const bSemConta = b.key in TIPO_SEM_CONTA ? 1 : 0;
        return aSemConta - bSemConta || b.total - a.total;
    });
};

const pct = (parte: number, todo: number) => (todo > 0 ? (parte / todo) * 100 : 0);

/** "R$ 750,00 (75.0%)" — o mesmo conteúdo do tooltip, sem o nome do estado (a cor já o diz). */
const rotuloFatia = (valor: number, p: number) => `${formatBRL(valor)} (${p.toFixed(1)}%)`;

/**
 * Fatia estreita não recebe texto: "R$ 1.234.567,89 (18.0%)" não cabe e sairia cortado no meio de
 * um número, o que é pior do que não mostrar. Abaixo disso o valor fica só no tooltip (e no
 * total do grupo, à direita do rótulo).
 */
const LARGURA_MINIMA_PARA_TEXTO = 22;

const Barra: React.FC<{ grupo: Grupo }> = ({ grupo }) => (
    <div className="mb-3">
        <div className="flex justify-between items-baseline text-xs mb-1">
            <span className="font-medium text-gray-700 dark:text-gray-200">{grupo.label}</span>
            <span className="text-gray-500">{formatBRL(grupo.total)}</span>
        </div>
        <div className="flex h-5 rounded overflow-hidden bg-gray-100 dark:bg-slate-700">
            {ORDEM.map(status => {
                const valor = grupo.porStatus[status];
                if (valor <= 0) return null;
                const p = pct(valor, grupo.total);
                return (
                    <div
                        key={status}
                        className={`${STATUS_META[status].bar} flex items-center justify-center`}
                        style={{ width: `${p}%` }}
                        // o número precisa estar acessível mesmo quando a fatia é estreita demais
                        // para caber texto dentro dela
                        title={`${STATUS_META[status].label}: ${formatBRL(valor)} (${p.toFixed(1)}%)`}
                    >
                        {p >= LARGURA_MINIMA_PARA_TEXTO && (
                            <span className="text-[10px] font-semibold text-white px-1 truncate whitespace-nowrap">
                                {rotuloFatia(valor, p)}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    </div>
);

/** Rosca em SVG: cada fatia é um arco desenhado por stroke-dasharray sobre o mesmo círculo. */
const Rosca: React.FC<{ porStatus: Record<PaymentStatus, number>; total: number }> = ({ porStatus, total }) => {
    const RAIO = 42;
    const CIRCUNFERENCIA = 2 * Math.PI * RAIO;
    let acumulado = 0;

    return (
        <svg viewBox="0 0 100 100" className="w-32 h-32" role="img" aria-label="Repartição do dia">
            <circle cx="50" cy="50" r={RAIO} fill="none" strokeWidth="14"
                className="stroke-gray-100 dark:stroke-slate-700" />
            {ORDEM.map(status => {
                const valor = porStatus[status];
                if (valor <= 0) return null;
                const fracao = valor / total;
                const arco = fracao * CIRCUNFERENCIA;
                const offset = acumulado * CIRCUNFERENCIA;
                acumulado += fracao;
                return (
                    <circle
                        key={status} cx="50" cy="50" r={RAIO} fill="none" strokeWidth="14"
                        stroke={STATUS_META[status].hex}
                        strokeDasharray={`${arco} ${CIRCUNFERENCIA - arco}`}
                        // -90° para a rosca começar no topo, e não às 3 horas
                        strokeDashoffset={-offset}
                        transform="rotate(-90 50 50)"
                    />
                );
            })}
        </svg>
    );
};

interface Props {
    rows: PaymentRow[];
}

const PaymentsPanel: React.FC<Props> = ({ rows }) => {
    const grupos = useMemo(() => agruparPagamentos(rows), [rows]);

    const { total, porStatus } = useMemo(() => {
        const acc: Record<PaymentStatus, number> = { PENDING: 0, MARKED: 0, IDENTIFIED: 0 };
        let soma = 0;
        for (const row of rows) {
            const parts = splitByStep(row);
            for (const status of ORDEM) acc[status] += parts[status];
            soma += row.amount || 0;
        }
        return { total: soma, porStatus: acc };
    }, [rows]);

    if (rows.length === 0) {
        return <p className="text-sm text-gray-500 py-4 text-center">Nenhum pagamento neste dia.</p>;
    }

    return (
        <div className="flex flex-col lg:flex-row gap-6">
            {/* 2/3: uma barra por conta (e por tipo sem conta) */}
            <div className="lg:w-2/3">
                {grupos.map(grupo => <Barra key={grupo.key} grupo={grupo} />)}
            </div>

            {/* 1/3: o dia inteiro */}
            <div className="lg:w-1/3 flex items-center gap-4 border-t lg:border-t-0 lg:border-l dark:border-slate-700 pt-4 lg:pt-0 lg:pl-6">
                <Rosca porStatus={porStatus} total={total} />
                <div className="text-xs space-y-1.5">
                    <div className="font-semibold text-gray-700 dark:text-gray-200 mb-1">
                        Total do dia: {formatBRL(total)}
                    </div>
                    {ORDEM.map(status => (
                        <div key={status} className="flex items-center gap-2">
                            <span className={`inline-block w-3 h-3 rounded-sm ${STATUS_META[status].bar}`} />
                            <span className="text-gray-600 dark:text-gray-300">
                                {STATUS_META[status].label}
                            </span>
                            <span className="font-medium">{formatBRL(porStatus[status])}</span>
                            <span className="text-gray-400">
                                ({pct(porStatus[status], total).toFixed(1)}%)
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PaymentsPanel;
